'use client';

import { Button, IcInfo, IconButton, Typography } from '@musinsa/mds';
import { Tooltip } from '@musinsa/mds-ui';
import { type ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface CouponHeaderCardData {
  salesRate: number;
  couponName: string;
  subtitleText: string;
  status: 'AVAILABLE' | 'RECEIVED' | 'EXHAUSTED' | 'EXPIRED';
}

interface CouponHeaderCardLayoutProps {
  data: CouponHeaderCardData;
  /** secondary 버튼 slot. EXPIRED 상태에서는 Layout 이 자체적으로 렌더하지 않는다. */
  secondaryButton?: ReactNode;
  /** info 아이콘 노출 여부 — visitor 는 항상, owner 는 EXPIRED 일 때만 */
  showInfoIcon?: boolean;
  onClickShop: () => void;
  /** info 아이콘 클릭 핸들러 — EXPIRED 가 아닌 상태에서 호출(쿠폰 사용 가이드 등). EXPIRED 는 컴포넌트가 자체 툴팁을 노출. */
  onClickGuide?: () => void;
  className?: string;
}

const EXPIRED_TOOLTIP_DURATION_MS = 5000;

export const CouponHeaderCardLayout = ({
  data,
  secondaryButton,
  showInfoIcon = false,
  onClickShop,
  onClickGuide,
  className,
}: CouponHeaderCardLayoutProps) => {
  const isExpired = data.status === 'EXPIRED';
  const [isExpiredTooltipOpen, setIsExpiredTooltipOpen] = useState(false);

  const handleClickInfo = () => {
    if (isExpired) {
      setIsExpiredTooltipOpen((prev) => !prev);
      return;
    }
    onClickGuide?.();
  };

  return (
    <div className={twMerge('flex flex-col bg-gray-100', className)}>
      <div className="flex flex-col px-4 pt-3 pb-1">
        <Typography variant="title18pxMed" colorScheme="black">
          {data.salesRate}%
        </Typography>
        <Typography variant="body13pxMed" colorScheme="black" className="mt-[4px]">
          {data.couponName}
        </Typography>
        <div className="mt-[2px]! flex items-center gap-0.5">
          <Typography variant="body13pxReg" colorScheme="gray-600">
            {data.subtitleText}
          </Typography>
          {showInfoIcon && (
            <div className="relative flex items-center">
              <IconButton size="size16x16" aria-label="쿠폰 사용 가이드" onClick={handleClickInfo}>
                <IcInfo width={16} height={16} strokeColor="gray-600" />
              </IconButton>
              {isExpired && isExpiredTooltipOpen && (
                <Tooltip
                  align="bottom center"
                  duration={EXPIRED_TOOLTIP_DURATION_MS}
                  onCloseCallback={() => setIsExpiredTooltipOpen(false)}
                  className="absolute! top-full! left-1/2! -translate-x-1/2! z-50! mt-[6px]! max-w-none!"
                  content={
                    <>
                      쿠폰 사용기간이 종료된 캠페인입니다.
                      <br />
                      상품 구매는 여전히 가능하니 지금 바로
                      <br />
                      확인해보세요.
                    </>
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start gap-1 px-4 py-3">
        {/* NOTE: Button(inline-flex) 을 직접 flex item 으로 두면 자식2(div) 와 측정 알고리즘이 달라 균등 분배가 깨짐. 두 자식 모두 block wrapper 로 통일하고 Button 은 w-full 로 채움 */}
        <div className="flex-1 min-w-0">
          <Button variant="secondary" className="w-full" size={32} onClick={onClickShop}>
            <Typography variant="body13pxMed">큐레이션 샵</Typography>
          </Button>
        </div>
        {!isExpired && secondaryButton && <div className="flex-1 min-w-0">{secondaryButton}</div>}
      </div>
    </div>
  );
};
